import { createContext, type ReactNode, useEffect, useMemo } from 'react';

import { useEndpoint } from 'api';
import { type ErrorObject, isError } from 'errors';
import { useRequiredContext } from 'hooks';
import type { Store } from 'models/api';

import { useSelectedStore } from './SelectedStoreContextProvider';

interface SelectedStoreInfoContextType {
	error: ErrorObject | undefined;
	isLoading: boolean;
	storeInfo: Store | undefined;
}
export const SelectedStoreInfoContext = createContext<
	SelectedStoreInfoContextType | undefined
>(undefined);

interface Props {
	children: ReactNode;
}

export function SelectedStoreInfoContextProvider({ children }: Props) {
	const { removeSelectedStore, selectedStore } = useSelectedStore();
	// TODO: useEndpoint wraps swr that has it's own context (used here indirectly)
	// so nothing needs to be in this context and this could be a hook.
	const {
		data: storeInfo,
		error,
		isLoading,
	} = useEndpoint(
		selectedStore?.id
			? {
					name: 'GetStore',
					pathParams: { storeId: selectedStore.id },
				}
			: null,
	);

	useEffect(() => {
		if (isError(error, 'ResponseError') && error.status === 400) {
			removeSelectedStore();
		}
	}, [error, removeSelectedStore]);

	const value = useMemo(
		() => ({ storeInfo, isLoading, error }),
		[storeInfo, isLoading, error],
	);

	return (
		<SelectedStoreInfoContext value={value}>
			{children}
		</SelectedStoreInfoContext>
	);
}
SelectedStoreInfoContextProvider.displayName =
	'SelectedStoreInfoContextProvider';

export function useSelectedStoreInfo() {
	return useRequiredContext(
		SelectedStoreInfoContext,
		'SelectedStoreInfoContext',
	);
}
